import cloneDeep from 'lodash/cloneDeep';
import rdiff from 'recursive-diff';
import { rdiffResult } from 'recursive-diff';
import { arrayMoveImmutable } from 'array-move';

import {
    AssessmentSummarySA,
    AtOrMediaEntity,
    QNA,
    SAVitalsHistory,
} from '../../api/fetchAssessmentSummary';
import { ComplimentaryDrugResponse } from '../../api/fetchComplimentaryDrugs';
import { Tool as PushVisitTool } from '../../api/pushVisit';
import { Drug, LabTest } from '../../api/fetchDrugsLabTestsFromSymDia';
import { RelatedSymDia } from '../../api/fetchSymDiaFromSymDia';
import { options, PropertyGroupEntity, SINCE_ID } from '../../api/fetchSymDiaProperties';
import { PadDForm, PrintConfig, Translate } from '../../api/pushVisit';
import {
    CUSTOM_VITALS_SAFE_RANGE,
    VITALS_WITHOUT_RANGE,
    OLD_HARDCODED_VITALS_TO_NEW_MAPPING,
    changeVitalIdFromVToLB,
} from '../../components/prescriptionPad/leftPanel/vitals';
import { convertToNewField } from '../../components/prescriptionPad/rightPanel/workspace/convertToNewField';
import {
    DELETE_ADVICE,
    DELETE_DIAGNOSE,
    DELETE_MEDICATION,
    DELETE_SYMPTOM,
    DELETE_LAB_TEST,
    FINISH_PRESCRIPTION,
    LOAD_PRESCRIPTION,
    LOAD_PRESCRIPTION_TEMPLATE,
    UPDATE_FOLLOWUP,
    DELETE_MEDICAL_HISTORY,
    CLEAR_MEDICAL_HISTORY,
    UPSERT_MEDICAL_HISTORY,
    UPSERT_MEDICAL_HISTORY_CARD_STATE,
    TOGGLE_NO_KNOWN_MEDICAL_HISTORY,
    UPDATE_SUGGESTED_DRUGS_LAB_TESTS,
    UPDATE_VITALS,
    UPSERT_ADVICE,
    UPSERT_DIAGNOSE,
    UPSERT_LAB_TEST,
    UPSERT_MEDICATION,
    UPSERT_SYMPTOM,
    UNDO_ACTION,
    UPDATE_EXAMINATION,
    UPDATE_RELATED_SYM_DIA,
    CLEAR_PRESCRIPTION,
    TOGGLE_USE_PRESCRIPTION_TEMPLATE_DIALOG,
    CLEAR_PRESCRIPTION_PAD,
    SHOW_DIALOG,
    HIDE_DIALOG,
    UPDATE_PP_NOTES,
    UPDATE_PP_PRIVATE_NOTES,
    UPDATE_DRUG_INTERACTIONS,
    UPDATE_SYM_DIA_PROPERTIES,
    UPDATE_COMPLIMENTARY_DRUG,
    SET_ASSESSMENT_SUMMARY,
    UPDATE_ASSESSMENT_SUMMARY_FETCH_STATE,
    UPDATE_PATIENT,
    UPDATE_REFERAL,
    APPLY_DRAFT,
    SET_TOAST_TYPE,
    UPDATE_DRAFT_ID,
    COPY_DURATION_IN_MED,
    SET_FOLLOWUP,
    UPDATE_TRANSLATE,
    UPDATE_INVENTORY,
    UPSERT_DYFORM,
    DELETE_DYFORM,
    UPDATE_PRINT_CONFIG,
    SWAP_PX_ELEMENTS,
    SWAP_MEDICAL_HISTORY_ELEMENTS,
    UPDATE_LABS_FOR_REFERAl,
    UPDATE_LAB_TO_REFER_ID,
    UPDATE_LAB_REFERAL,
    UPSERT_LAB_VITAL,
    DELETE_LAB_VITAL,
    UPDATE_PT_PAST_VITALS,
    DELETE_VITALS,
    CLEAR_VITALS,
    UPDATE_VITALS_V0,
    UPDATE_EXAMINATION_FINDINGS,
    UPSERT_PATIENT_DETAILS,
    MAGIC_BOX_ACTIVE_TAB,
    DELETE_ADVICE_PERMANENTLY,
    EDIT_ADVICE,
    PUSH_DIFF,
    UPDATE_DY_FORM_PRINT_CONFIG,
    LOAD_PRINT_CONFIG,
    COPY_LAB_VITAL_DATE,
    SET_VITALS,
    COPY_SINCE_IN_SY,
    COPY_SINCE_IN_DY,
    UPSERT_PRESCRIPTION,
    SANITIZE_PRESCRIPTION,
    SANITIZE_FINAL_PRESCRIPTION,
    LOAD_TRANSLATE,
    SET_LAB_VITALS,
    UPSERT_ATTACHMENT,
    UPDATE_MEDICATIONS,
    UPDATE_LAB_TESTS,
    SET_DRUGS_SUBSTITUTE,
    REPLACE_HE_MEDICATION,
    SET_DOCTOR_PACKAGES,
    UPSERT_DENTAL_EXAMINATION,
    UPSERT_DENTAL_PROCEDURE,
    COPY_DENTAL_PROCEDURE_DATE,
    DELETE_DENTAL_PROCEDURE,
    DELETE_DENTAL_EXAMINATION,
    COPY_DENTAL_EXAMINATION_DATE,
    DELETE_DENTAL_EXAMINATIONS,
    UPSERT_ADMINISTERED_VACCINES,
    CLEAR_ADMINISTERED_VACCINES,
    UPDATE_ICD_CODES,
    SET_CHART_TYPE,
    SET_PROCESSED_DENTAL_EXAMINATIONS,
    SET_PATIENT_HEALTH_SUMMARY,
    UPSERT_MILESTONES,
    SET_MILESTONES,
    UPDATE_MILESTONE,
    EDIT_DENTAL_EXAMINATIONS_GROUP,
    COPY_TEST_ON_LAB_INVESTIGATIONS,
    COPY_REPEAT_ON_LAB_INVESTIGATIONS,
    SET_INJECTION_ELEMENTS,
    UPSERT_INJECTION,
    DELETE_INJECTION,
    UPSERT_ELEMENT,
    SET_ONGOING_TREATMENT_PLANS,
    UPSERT_DENTAL_PROCEDURES,
    OP_FGP_COPY_FROM,
    DELETE_ELEMENT,
    COPY_KEY_TO_ALL,
    UPSERT_IPD_ADMISSION,
    UPSERT_CARE_CANVAS,
    UPSERT_RELATED_PROFILE_DATA,
    ApplyDraftAction,
    UPSERT_PRESCRIPTION_V2,
    UPSERT_CURRENT_MEDICATIONS,
    SET_MED_ROUTE_ENABLED,
} from '../actions/prescriptionPad';
import {
    DentalExamination,
    DentalProcedure,
    DiagnosisEntityOrSymptomsEntity,
    Duration,
    Followup,
    MedicationsEntity,
    OpSpecElement,
    Prescription,
    SelectionEntity,
    toMultiRerferObj,
    Tool,
    VitalsEntity,
} from '../types/prescription';

import {
    cloneAndDelete,
    cloneAndUpsert,
    cloneAndUpsertArray,
    getAgeInYears,
    getUnit,
    medWithQuantity,
    sanitizeMed,
    uniqueBy,
} from '../utils';
import { Res } from '../../api/fetchSpecialityForDoctor';
import { toPxLabVitalFromApiObj } from '../../components/prescriptionPad/rightPanel/workspace/padV2/LabVitalAutoSuggest';
import { PastVitalsData } from '../../api/fetchPastVitalsByPtOid';
import { LOCAL_ID_PREFIX, MED_TABLE_COL_QUANTITY_KEY, VITALS } from '../../constants';
import { Inventory } from '../../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import {
    IVaccineStatus,
    MHCWithMilestone,
    MHCWithVaccination,
    MHItem2,
} from '../types/medicalHistory';
import { UIDocumentEntity } from '../../pages/MedicalRecordsRightPanel';
import { MAGIC_BOX_COMPONENTS } from '../../pages/prescriptionPad/magicBox/MAGIC_BOX_COMPONENTS';
import { UPSERT_GENIE_SETTINGS } from '../actions/session';
import { Actions } from '../actions';
import { Response } from '../../api/fetchPxDraft';
import { CodedMedicine, SubstitutesResponse } from '../../api/fetchDrugsSubstitute';
import { DRUG_DOSAGE_DURATION_TIMING_ENUM } from '../../api/fetchMatchingDrugsLabTests';
import { HXNG_MAPPED, HXNG_STATUS } from '../../api/fetchDrugsAndLabTestsStatus';
import { POPUP, SUBSTITUTE } from '../../components/prescriptionPad/trackSource';
import { DoctorPackages } from '../../api/fetchDoctorPackages';
import {
    ChartType,
    ChartTypeEnum,
} from '../../components/prescriptionPad/leftPanel/DentalChart/DentalChart';
import {
    ProcessedDentalExaminations,
    TeethToSurfacesObject,
} from '../../components/prescriptionPad/leftPanel/DentalChart/DentalExamination';
import { KeyToSetObject } from '../../components/prescriptionPad/leftPanel/DentalChart/Chart';
import { PatientHealthSummaryWithPoolingResponse } from '../../api/getPatientHealthSummaryWithPooling';
import uniqBy from 'lodash/uniqBy';
import { allSurfacesIdToName } from '../../components/prescriptionPad/leftPanel/DentalChart/constants';
import { TreatmentPlan } from '../../api/getTreatmentPlans';
import { getColumns, getCopiedvalue, getDefaulOPValue, propertyMap } from '../../pages/opUtils';
import PadGenieAutoSuggestFuseManager from '../../components/prescriptionPad/PadGenieAutoSuggestFuseManager';
import { FETCH_STATE } from '../../components/common/PatientOverviewUtils';
import { CareCanvas } from '../types/CareCanvas';

const TRACKING_SOURCE_TEMPLATE = 'TEMPLATE';

export type DrugInteractionException = {
    type: string;
    drugs: {
        id: string;
        name: string;
        generic: string;
    }[];
};

export type AST = {
    sa?: AssessmentSummarySA;
    qna?: QNA[];
    fetchState: 'loading' | 'success' | 'error';
    vitals?: SAVitalsHistory;
    media?: AtOrMediaEntity[];
};

export type ToastType = 'summary' | 'push-success' | undefined;

export type PatientDetailsVisits = {
    state: FETCH_STATE;
    data?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string;
        flavour?: string;
        translateLang?: string;
    })[];
};

export type PatientDetailsMR = {
    state: FETCH_STATE;
    data?: UIDocumentEntity[];
    lastUpdatedAt?: string;
};

export type PatientDetails = {
    visits?: PatientDetailsVisits;
    mr?: PatientDetailsMR;
    allPtIds?: string[];
};

export type PatientDetailsPastVitals = {
    state: FETCH_STATE;
    data?: PastVitalsData;
};

export type RelatedProfileData = {
    visits?: PatientDetailsVisits;
    mr?: PatientDetailsMR;
    pastVitals?: PatientDetailsPastVitals;
    allPtIds?: string[];
};

export interface PrescriptionPadReducerV1 {
    prescription?: Prescription;
    suggestedDrugs?: Drug[];
    suggestedLabTests?: LabTest[];
    relatedSymptons?: RelatedSymDia[];
    relatedDiagnosis?: RelatedSymDia[];
    /**
     * @deprecated was used in padv1
     */
    isUseTemplateDialogVisible?: boolean;
    drugInteractions?: DrugInteractionException[];
    symDiaProperties?: { [k: string]: PropertyGroupEntity[] };
    complimentaryDrugs?: ComplimentaryDrugResponse;
    symDiaICDCodes?: { [k: string]: { icd10_code: string; icd10_name: string } };
    diaglogs: {
        exa: boolean;
        vitals: boolean;
        ph: boolean;
        ppNotes: boolean;
        referal: boolean;
        summary: boolean;
        labVital: boolean;
        longitudinalVitalView: boolean;
        abdmBanner: boolean;
        healthWeek: boolean;
        substitutions: boolean;
        supportTickets: boolean;
        troubleshootMySystem: boolean;
    };
    versions: rdiffResult[][];
    assessmentSummary?: AST;
    toastVisible?: ToastType;
    translate?: Translate;
    printConfigs?: PrintConfig;
    labsAvailableToRefer?: Res;
    selectedLabToReferId?: string;
    ptPastVitals?: PastVitalsData;
    medicalHistoryCardState?: boolean;
    noKnownMedicalHistory?: boolean;
    magicBoxActiveTab: string;
    inventory?: Inventory;
    patientDetails?: PatientDetails;
    draft?: Response;
    drugsSubstitute?: SubstitutesResponse;
    doctorPackages?: DoctorPackages;
    chartType?: ChartType;
    processedDentalExaminations?: ProcessedDentalExaminations[];
    isFetchingPatientHealthSummary: boolean;
    patientHealthSummary?: PatientHealthSummaryWithPoolingResponse;
    ongoingTreatmentPlans?: TreatmentPlan[];
    relatedProfilesData?: {
        [k: string]: RelatedProfileData;
    };
    medRouteEnabled: boolean;
}

export const initalSuggestions = {
    suggestedDrugs: [],
    suggestedLabTests: [],
    relatedSymptons: [],
    relatedDiagnosis: [],
};

export const initialPrescriptionState: PrescriptionPadReducerV1 = {
    prescription: undefined,
    ...initalSuggestions,
    isUseTemplateDialogVisible: false,
    diaglogs: {
        exa: false,
        vitals: false,
        ph: false,
        ppNotes: false,
        referal: false,
        summary: false,
        labVital: false,
        longitudinalVitalView: false,
        abdmBanner: false,
        healthWeek: false,
        substitutions: false,
        supportTickets: false,
        troubleshootMySystem: false,
    },
    versions: [],
    medicalHistoryCardState: true,
    noKnownMedicalHistory: false,
    magicBoxActiveTab: MAGIC_BOX_COMPONENTS.VISITS,
    patientDetails: {
        visits: {
            state: FETCH_STATE.FETCHING,
        },
        mr: {
            state: FETCH_STATE.FETCHING,
        },
    },
    isFetchingPatientHealthSummary: true,
    medRouteEnabled: false,
};

export default (state = initialPrescriptionState, action: Actions): PrescriptionPadReducerV1 => {
    switch (action.type) {
        case UPSERT_PATIENT_DETAILS:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                patientDetails: {
                    ...state.patientDetails,
                    ...action.details,
                },
            };
        case UPDATE_PT_PAST_VITALS:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                ptPastVitals: action.ptPastVitals,
            };
        case UPDATE_LAB_REFERAL:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labRefer: action.labRefer,
                },
            };
        case UPDATE_LAB_TO_REFER_ID:
            return {
                ...state,
                selectedLabToReferId: action.id,
            };
        case UPDATE_LABS_FOR_REFERAl:
            return {
                ...state,
                labsAvailableToRefer: action.labs,
            };
        case SWAP_PX_ELEMENTS:
            if (!state.prescription) {
                return state;
            }

            const el = state.prescription[action.what];

            if (!el || !Array.isArray(el) || el.length < action.from || el.length < action.to) {
                return state;
            }

            const newData = arrayMoveImmutable([...el], action.from, action.to).filter(
                (el) => !!el,
            );

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    [action.what]: newData,
                },
            };

        case SWAP_MEDICAL_HISTORY_ELEMENTS:
            if (!state.prescription?.patientHistory) {
                return state;
            }

            const phdata = state.prescription.patientHistory[action.what];

            if (
                !phdata ||
                !Array.isArray(phdata) ||
                phdata.length < action.from ||
                phdata.length < action.to
            ) {
                return state;
            }

            const newPhData = arrayMoveImmutable([...phdata], action.from, action.to).filter(
                (phdata) => !!phdata,
            );

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    patientHistory: {
                        ...state.prescription.patientHistory,
                        [action.what]: newPhData,
                    },
                },
            };

        case UPDATE_DY_FORM_PRINT_CONFIG:
            return {
                ...state,
                printConfigs: {
                    ...state.printConfigs,
                    dyform: {
                        ...state.printConfigs?.dyform,
                        [action.id]: action.isPrintEnabled,
                    },
                },
            };

        case LOAD_PRINT_CONFIG:
        case UPDATE_PRINT_CONFIG:
            return {
                ...state,
                printConfigs: action.printConfigs,
            };

        case UPSERT_DYFORM:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dyform: cloneAndUpsert(action.dyform, state.prescription.dyform).filter(
                        (t) => t.elements?.length,
                    ),
                },
            };

        case DELETE_DYFORM:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dyform: cloneAndDelete(action.id, state.prescription.dyform),
                },
            };

        case COPY_DURATION_IN_MED: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    medications: state.prescription.medications?.map((m) =>
                        medWithQuantity({ ...m, duration: action.duration }, action.reCalculateQt),
                    ),
                },
            };
        }

        case COPY_TEST_ON_LAB_INVESTIGATIONS: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labTests: state.prescription.labTests?.map((lab) => ({
                        ...lab,
                        test_on: action.test_on,
                    })),
                },
            };
        }

        case COPY_REPEAT_ON_LAB_INVESTIGATIONS: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labTests: state.prescription.labTests?.map((lab) => ({
                        ...lab,
                        repeat_on: action.repeat_on,
                    })),
                },
            };
        }

        case COPY_SINCE_IN_SY:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    symptoms: state.prescription.symptoms?.map((i) =>
                        getSyDyEntity(i, action.since),
                    ),
                },
            };

        case COPY_SINCE_IN_DY:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    diagnosis: state.prescription.diagnosis?.map((i) =>
                        getSyDyEntity(i, action.since),
                    ),
                },
            };

        case LOAD_TRANSLATE:
        case UPDATE_TRANSLATE:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                translate: action.translate,
            };

        case UPDATE_INVENTORY:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                inventory: action.inventory,
            };

        case UPDATE_DRAFT_ID: {
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    meta: {
                        ...state.prescription.meta,
                        draftId: action.draftId,
                    },
                },
            };
        }
        case OP_FGP_COPY_FROM:
            const rx = state.prescription;
            if (!rx) {
                return state;
            }

            let opFinalPrescription: OpSpecElement[] | undefined = getDefaulOPValue(
                'opFinalPrescription',
            );
            const opFinalPrescriptionColumns = getColumns('opFinalPrescription');
            const keys = opFinalPrescriptionColumns.map((i) => i.key);
            opFinalPrescription = opFinalPrescription.map((i) => {
                const item = i;
                keys.forEach((key) => {
                    // Sorry for this hack, but it is needed to get the value from the correct object
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    item[key] = {
                        ...getCopiedvalue(action.from, i.id, rx, key),
                        ...propertyMap['opFinalPrescription'][i.id][key],
                    };
                });
                return item;
            });

            return {
                ...state,
                // Sorry for this hack, but it is needed to get the value from the correct object
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                prescription: {
                    ...state.prescription,
                    opFinalPrescription: opFinalPrescription,
                },
            };

        case SET_TOAST_TYPE:
            return {
                ...state,
                toastVisible: action.toast,
                draft: action.toast ? state.draft : undefined,
            };
        case APPLY_DRAFT:
            if (!state.prescription) {
                return state;
            }
            const { draft, treatmentProcedures } = action;

            const draftVaccinationHistory = action.draft.data.tool.allVaccines;
            const draftAdministeredVaccines = action.draft.data.tool.administeredVaccines;

            if (state.prescription.meta?.draftId === draft.id) {
                return {
                    ...state,
                    prescription: {
                        ...state.prescription,
                        dentalProcedures:
                            Array.isArray(treatmentProcedures) && treatmentProcedures.length > 0
                                ? cloneAndUpsertArray(
                                      state.prescription.dentalProcedures || [],
                                      treatmentProcedures,
                                  )
                                : state.prescription.dentalProcedures,
                        allVaccines: cloneAndUpsertArray(
                            draftVaccinationHistory || [],
                            state.prescription.allVaccines || [],
                        ),
                        administeredVaccines: cloneAndUpsertArray(
                            draftAdministeredVaccines || [],
                            state.prescription.administeredVaccines || [],
                        ),
                    },
                };
            }

            const dyform = cloneAndUpsertArray(
                state.prescription.dyform || [],
                action.draft.data.tool.dyform,
            );

            const toolDyform = action.draft.data.tool.dyform;

            dyform.forEach((i) => {
                i.elements = cloneAndUpsertArray(
                    i.elements || [],
                    toolDyform?.find((j) => j.id === i.id)?.elements,
                );
            });

            const toolFollowup = action.draft.data.tool.followup;

            const newDentalExaminations =
                Number(action.draft.data.tool.dentalExaminations?.length) > 0
                    ? action.draft.data.tool.dentalExaminations
                    : state.prescription.dentalExaminations;

            const careCanvas = Array.isArray(action.draft.data.tool.careCanvas)
                ? action.draft.data.tool.careCanvas
                : state.prescription.careCanvas;

            action.draft.data.tool.vaccinationHistory = draftVaccinationHistory;

            return {
                ...state,
                prescription: upsertedRx(
                    state.prescription,
                    action.draft.data.tool,
                    dyform,
                    newDentalExaminations,
                    careCanvas,
                    action.draft.id,
                    cloneAndUpsertArray(draftVaccinationHistory || [], state.prescription.allVaccines || []),
                    cloneAndUpsertArray(draftAdministeredVaccines || [], state.prescription.administeredVaccines || []),
                    toolFollowup,
                ),
                draft: action.draft,
                processedDentalExaminations: getProcessedDentalExaminations({
                    dentalExaminations: newDentalExaminations,
                } as Prescription),
            };
        case UPDATE_REFERAL:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    refer: action.refer,
                },
            };

        case UPDATE_PATIENT:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    patient: action.patient,
                },
            };
        case UPDATE_ASSESSMENT_SUMMARY_FETCH_STATE:
            return {
                ...state,
                assessmentSummary: { fetchState: action.fetchState },
            };
        case SET_ASSESSMENT_SUMMARY:
            return {
                ...state,
                assessmentSummary: {
                    sa: action.data.sa,
                    qna: action.data.qna,
                    vitals: action.data.vitals,
                    media: action.data.media || undefined,
                    fetchState: 'success',
                },
            };
        case UPDATE_COMPLIMENTARY_DRUG:
            return {
                ...state,
                complimentaryDrugs: action.complimentaryDrugs,
            };
        case UPDATE_SYM_DIA_PROPERTIES:
            return {
                ...state,
                symDiaProperties: { ...state.symDiaProperties, ...action.symDiaProperties },
            };
        case UPDATE_ICD_CODES:
            if (!state.prescription) {
                return state;
            }

            const allSymDiaICDCodes = {
                ...(state.symDiaICDCodes || {}),
                ...(action.symDiaICDCodes || {}),
            };

            return {
                ...state,
                symDiaICDCodes: allSymDiaICDCodes,
                prescription: {
                    ...state.prescription,
                    symptoms: state?.prescription.symptoms?.map((sym) => ({
                        ...sym,
                        icd10_code: allSymDiaICDCodes?.[sym.id]?.icd10_code,
                        icd10_name: allSymDiaICDCodes?.[sym.id]?.icd10_name,
                    })),
                    diagnosis: state?.prescription.diagnosis?.map((sym) => ({
                        ...sym,
                        icd10_code: allSymDiaICDCodes?.[sym.id]?.icd10_code,
                        icd10_name: allSymDiaICDCodes?.[sym.id]?.icd10_name,
                    })),
                },
            };
        case UPDATE_DRUG_INTERACTIONS:
            return {
                ...state,
                drugInteractions: action.interactions,
            };
        case SHOW_DIALOG: {
            return {
                ...state,
                diaglogs: {
                    exa: action.which === 'exa' ? true : state.diaglogs.exa,
                    vitals: action.which === 'vitals' ? true : state.diaglogs.vitals,
                    ph: action.which === 'ph' ? true : state.diaglogs.ph,
                    ppNotes: action.which === 'ppNotes' ? true : state.diaglogs.ppNotes,
                    referal: action.which === 'referal' ? true : state.diaglogs.referal,
                    summary: action.which === 'summary' ? true : state.diaglogs.summary,
                    labVital: action.which === 'lab-vital' ? true : state.diaglogs.labVital,
                    longitudinalVitalView:
                        action.which === 'longitudinal-vital-view'
                            ? true
                            : state.diaglogs.longitudinalVitalView,
                    abdmBanner: action.which === 'abdm-banner' ? true : state.diaglogs.abdmBanner,
                    healthWeek: action.which === 'health-week' ? true : state.diaglogs.healthWeek,
                    substitutions:
                        action.which === 'substitutions' ? true : state.diaglogs.substitutions,
                    supportTickets:
                        action.which === 'support-tickets' ? true : state.diaglogs.supportTickets,
                    troubleshootMySystem:
                        action.which === 'troubleshoot-my-system'
                            ? true
                            : state.diaglogs.troubleshootMySystem,
                },
            };
        }
        case HIDE_DIALOG: {
            return {
                ...state,
                diaglogs: {
                    exa: action.which === 'exa' ? false : state.diaglogs.exa,
                    vitals: action.which === 'vitals' ? false : state.diaglogs.vitals,
                    ph: action.which === 'ph' ? false : state.diaglogs.ph,
                    ppNotes: action.which === 'ppNotes' ? false : state.diaglogs.ppNotes,
                    referal: action.which === 'referal' ? false : state.diaglogs.referal,
                    summary: action.which === 'summary' ? false : state.diaglogs.summary,
                    labVital: action.which === 'lab-vital' ? false : state.diaglogs.labVital,
                    longitudinalVitalView:
                        action.which === 'longitudinal-vital-view'
                            ? false
                            : state.diaglogs.longitudinalVitalView,
                    abdmBanner: action.which === 'abdm-banner' ? false : state.diaglogs.abdmBanner,
                    healthWeek: action.which === 'health-week' ? false : state.diaglogs.healthWeek,
                    substitutions:
                        action.which === 'substitutions' ? false : state.diaglogs.substitutions,
                    supportTickets:
                        action.which === 'support-tickets' ? false : state.diaglogs.supportTickets,
                    troubleshootMySystem:
                        action.which === 'troubleshoot-my-system'
                            ? false
                            : state.diaglogs.troubleshootMySystem,
                },
            };
        }
        // TODO
        case CLEAR_PRESCRIPTION_PAD: {
            PadGenieAutoSuggestFuseManager.clearAll();
            return initialPrescriptionState;
        }
        case UPDATE_PP_NOTES: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    prescriptionNotes: action.pNotes,
                },
            };
        }

        case UPDATE_PP_PRIVATE_NOTES: {
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    privateNotes: action.privateNotes,
                },
            };
        }
        case TOGGLE_USE_PRESCRIPTION_TEMPLATE_DIALOG: {
            return {
                ...state,
                isUseTemplateDialogVisible: action.isVisible,
            };
        }
        // TODO
        case CLEAR_PRESCRIPTION:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                ...initalSuggestions,
                assessmentSummary: state.assessmentSummary,
                prescription: {
                    ...state.prescription,
                    examinations: undefined,
                    diagnosis: undefined,
                    symptoms: undefined,
                    vitals: undefined,
                    medications: undefined,
                    labTests: undefined,
                    followup: undefined,
                    advices: undefined,
                    prescriptionNotes: undefined,
                    privateNotes: undefined,
                    refer: undefined,
                    dyform: undefined,
                    labVitals: undefined,
                    dentalExaminations: undefined,
                    dentalProcedures: undefined,
                    opVision: undefined,
                    opIop: undefined,
                    opCurrentSpec: undefined,
                    opSubjectiveRefraction: undefined,
                    opAutoRefraction: undefined,
                    opFinalPrescription: undefined,
                    injections: undefined,
                    procedures: undefined,
                },
                drugInteractions: undefined,
                processedDentalExaminations: undefined,
            };
        case PUSH_DIFF: {
            return {
                ...state,
                versions: [...state.versions, action.diff],
            };
        }
        case UNDO_ACTION: {
            const versions = [...state.versions];
            if (versions.length === 0 || !state.prescription) {
                return state;
            }

            const diff = versions.pop();
            if (!diff) {
                return state;
            }

            const newPrescription = rdiff.applyDiff(cloneDeep(state.prescription), diff);
            return {
                ...state,
                prescription: cloneDeep({ ...newPrescription }),
                versions: [...versions],
            };
        }
        case LOAD_PRESCRIPTION:
            // this migration of vitals means from FE vitals ids to BE vitals ids
            const migratedVitalsPrescription = migrateVitalsToBe(action.prescription);

            migratedVitalsPrescription.medications = migratedVitalsPrescription.medications?.map(
                (i) => ({
                    ...i,
                    hxng_mapped: undefined,
                    hxng_status: undefined,
                }),
            );
            const chartType = getChartType(action.prescription);
            const processedDentalExaminations = getProcessedDentalExaminations(action.prescription);
            // TODO
            return {
                ...initialPrescriptionState,
                symDiaProperties: state.symDiaProperties,
                symDiaICDCodes: state.symDiaICDCodes,
                prescription: {
                    ...migratedVitalsPrescription,
                    refer: toMultiRerferObj(action.prescription.refer),
                },
                translate: action.translate || state.translate,
                // isUseTemplateDialogVisible,
                diaglogs: state.diaglogs,
                chartType: chartType,
                processedDentalExaminations: processedDentalExaminations,
            };

        case SANITIZE_PRESCRIPTION: {
            if (!state.prescription) {
                return state;
            }

            const qtColumnEnabled = action.medElements.find(
                (ele) => ele.id === MED_TABLE_COL_QUANTITY_KEY,
            )?.isShown;

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    medications: state.prescription.medications?.map((medication) =>
                        medWithQuantity(
                            sanitizeMed(medication, action.medElements, state.medRouteEnabled),
                            qtColumnEnabled,
                            !!medication.quantity,
                        ),
                    ),
                },
            };
        }

        case UPSERT_PRESCRIPTION: {
            if (!state.prescription) {
                return state;
            }
            const processedDentalExaminations = getProcessedDentalExaminations(
                action.prescriptionElements as Prescription,
            );
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    ...action.prescriptionElements,
                },
                processedDentalExaminations: processedDentalExaminations,
            };
        }

        case UPSERT_PRESCRIPTION_V2: {
            if (!state.prescription) {
                return state;
            }
            const updatedRx = upsertedRx(state.prescription, action.prescriptionElements);
            const processedDentalExaminations = getProcessedDentalExaminations(
                updatedRx as Prescription,
            );

            return {
                ...state,
                prescription: updatedRx,
                processedDentalExaminations: processedDentalExaminations,
            };
        }

        case UPDATE_VITALS: {
            if (!state.prescription) {
                return state;
            }
            const updatedArr = Array.from(state.prescription.vitals || []);
            if (action.index !== undefined) {
                updatedArr.splice(action.index, 1, action.vitals);
            }
            const gender: string | undefined =
                state.prescription.patient.profile.personal.gender?.[0];
            const gen = gender?.toLowerCase();
            if (CUSTOM_VITALS_SAFE_RANGE[action.vitals.id]?.[gen]) {
                action.vitals.value.safe = CUSTOM_VITALS_SAFE_RANGE[action.vitals.id][gen];
            }
            const { doctor } = state.prescription;
            const businessId = doctor?.profile?.professional?.business?.id || '';
            if (VITALS_WITHOUT_RANGE[businessId]?.has(action.vitals.id)) {
                delete action.vitals.value.safe;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    vitals:
                        action.index !== undefined && state.prescription.vitals
                            ? updatedArr
                            : cloneAndUpsert(action.vitals, state.prescription.vitals),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case SET_VITALS: {
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    vitals: action.vitals,
                },
            };
        }
        case DELETE_VITALS:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    vitals:
                        action.index !== undefined && state.prescription.vitals
                            ? (state.prescription.vitals.splice(action.index, 1),
                              [...state.prescription.vitals].filter(Boolean))
                            : cloneAndDelete(action.id, state.prescription.vitals),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        case CLEAR_VITALS:
            if (!state.prescription) {
                return state;
            }

            const vitalsToPersist = action.deletedVitals
                ? state.prescription.vitals?.filter((v) => !action.deletedVitals?.includes(v.id))
                : [];

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    vitals: vitalsToPersist,
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };

        case UPDATE_EXAMINATION:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    examinations: action.data,
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };

        case UPDATE_EXAMINATION_FINDINGS:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    examinations:
                        action.index !== undefined && state.prescription.examinations
                            ? ((state.prescription.examinations[action.index] = action.data),
                              [...state.prescription.examinations])
                            : cloneAndUpsert(action.data, state.prescription.examinations),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };

        case UPSERT_LAB_VITAL: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labVitals:
                        action.index !== undefined && state.prescription.labVitals
                            ? ((state.prescription.labVitals[action.index] = action.element),
                              [...state.prescription.labVitals])
                            : cloneAndUpsert(action.element, state.prescription.labVitals),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }

        case COPY_LAB_VITAL_DATE:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labVitals: state.prescription.labVitals?.map((i) => ({
                        ...i,
                        date: action.date,
                    })),
                },
            };

        case DELETE_LAB_VITAL: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labVitals:
                        action.index !== undefined && state.prescription.labVitals
                            ? (state.prescription.labVitals.splice(action.index, 1),
                              [...state.prescription.labVitals].filter(Boolean))
                            : cloneAndDelete(action.id, state.prescription.labVitals),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }

        case SET_LAB_VITALS: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labVitals: action.labVitals,
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }

        case UPSERT_SYMPTOM: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    symptoms:
                        action.index !== undefined &&
                        state.prescription.symptoms &&
                        state.prescription.symptoms?.length > action.index
                            ? ((state.prescription.symptoms[action.index] = action.sysptom),
                              [...state.prescription.symptoms])
                            : cloneAndUpsert(action.sysptom, state.prescription.symptoms),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case UPSERT_ELEMENT: {
            if (!state.prescription) {
                return state;
            }

            const elementsArray = state.prescription[action.key];

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    [action.key]:
                        action.index !== undefined &&
                        elementsArray &&
                        (elementsArray?.length > action.index || action.insert)
                            ? ((elementsArray[action.index] = action.value), [...elementsArray])
                            : action.insert && elementsArray
                            ? ((elementsArray[elementsArray.length] = action.value),
                              [...elementsArray])
                            : cloneAndUpsert(action.value, elementsArray),
                },
                toastVisible: undefined,
            };
        }
        case DELETE_ELEMENT: {
            if (!state.prescription) {
                return state;
            }
            const elementsArray = state.prescription[action.key];
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    [action.key]: elementsArray
                        ? (elementsArray.splice(action.index, 1),
                          [...elementsArray].filter(Boolean))
                        : [],
                },
            };
        }
        case COPY_KEY_TO_ALL:
            if (!state.prescription) {
                return state;
            }
            const elementsArray = state.prescription[action.key];
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    [action.key]: elementsArray?.map((i) => ({
                        ...i,
                        [action.ele_key]: action.ele_value,
                    })),
                },
            };
        case DELETE_SYMPTOM: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    symptoms: cloneAndDelete(action.sysptomId, state.prescription.symptoms),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case UPSERT_DIAGNOSE: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    diagnosis:
                        action.index !== undefined &&
                        state.prescription.diagnosis &&
                        state.prescription.diagnosis?.length > action.index
                            ? ((state.prescription.diagnosis[action.index] = action.diagnose),
                              [...state.prescription.diagnosis])
                            : cloneAndUpsert(action.diagnose, state.prescription.diagnosis),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case DELETE_DIAGNOSE: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    diagnosis: cloneAndDelete(action.diagnoseId, state.prescription.diagnosis),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case UPSERT_MEDICATION: {
            if (!state.prescription) {
                return state;
            }

            const actionMeds = Array.isArray(action.medication)
                ? action.medication
                : [action.medication];

            const actionMedIds = actionMeds.map((med) => med.id);
            const pxMedsIds = new Set(state.prescription.medications?.map((med) => med.id) || []);

            action._isNewMedIntroduced =
                actionMedIds.find((id) => !pxMedsIds.has(id)) !== undefined;

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    medications:
                        action.index !== undefined &&
                        state.prescription.medications &&
                        state.prescription.medications?.length > action.index &&
                        actionMeds.length === 1
                            ? ((state.prescription.medications[action.index] = actionMeds[0]),
                              uniqueBy('id', [...state.prescription.medications]))
                            : cloneAndUpsertArray(actionMeds, state.prescription.medications),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case UPSERT_INJECTION: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    injections:
                        action.index !== undefined &&
                        state.prescription.injections &&
                        state.prescription.injections?.length > action.index
                            ? ((state.prescription.injections[action.index] = action.injection),
                              uniqueBy('id', [...state.prescription.injections]))
                            : cloneAndUpsert(action.injection, state.prescription.injections),
                },
            };
        }

        case UPSERT_ATTACHMENT: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    attachment: action?.attachment,
                },
            };
        }

        case UPSERT_GENIE_SETTINGS: {
            if (!action.setting.medElements) {
                return state;
            }
            if (!state.prescription) {
                return state;
            }
            const qtColumnEnabled = action.setting.medElements.find(
                (ele) => ele.id === MED_TABLE_COL_QUANTITY_KEY,
            )?.isShown;
            const actionMedRouteEnabled = action.setting.medRouteEnabled;

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    medications: state.prescription.medications?.map((t) =>
                        medWithQuantity(
                            sanitizeMed(
                                t,
                                action.setting.medElements,
                                actionMedRouteEnabled ?? state.medRouteEnabled,
                            ),
                            qtColumnEnabled,
                        ),
                    ),
                },
                ...(actionMedRouteEnabled ? { medRouteEnabled: actionMedRouteEnabled } : {}),
            };
        }

        case DELETE_MEDICATION: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    medications: cloneAndDelete(
                        action.medicationId,
                        state.prescription.medications,
                    ),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }

        case UPSERT_LAB_TEST: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labTests:
                        action.index !== undefined &&
                        state.prescription.labTests &&
                        state.prescription.labTests?.length > action.index
                            ? ((state.prescription.labTests[action.index] = action.labTest),
                              [...state.prescription.labTests])
                            : cloneAndUpsert(action.labTest, state.prescription.labTests),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case DELETE_LAB_TEST: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labTests: cloneAndDelete(action.id, state.prescription.labTests),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case SET_FOLLOWUP:
        case UPDATE_FOLLOWUP: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    followup: { date: action.date, optionId: action.optionId, notes: action.notes },
                },
            };
        }
        case UPSERT_MEDICAL_HISTORY:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    patientHistory: {
                        ...state.prescription.patientHistory,
                        [action.cat]:
                            action.index !== undefined &&
                            state.prescription.patientHistory?.[action.cat] &&
                            (state.prescription.patientHistory?.[action.cat] || []).length >
                                action.index
                                ? (((state.prescription.patientHistory[action.cat] as MHItem2[])[
                                      action.index
                                  ] = action.item),
                                  [
                                      ...(state.prescription?.patientHistory[
                                          action.cat
                                      ] as MHItem2[]),
                                  ])
                                : cloneAndUpsert(
                                      { ...action.item, reportedAt: new Date().toISOString() },
                                      (state.prescription.patientHistory || {})[action.cat],
                                  ),
                    },
                },
            };

        case UPSERT_ADMINISTERED_VACCINES:
            if (!state.prescription) {
                return state;
            }

            const ids = action.administeredVaccines?.map((v) => v.id);

            const restVaccs = state.prescription.administeredVaccines
                ? state.prescription.administeredVaccines.filter((v) => !ids?.includes(v.id))
                : [];

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    administeredVaccines: [...restVaccs, ...(action.administeredVaccines ?? [])],
                    allVaccines: action.allVaccines,
                },
            };

        case UPSERT_MILESTONES:
            if (!state.prescription) {
                return state;
            }

            const milestoneIds = action.milestones?.map((v) => v.id);

            const restMilestones = state.prescription.milestones
                ? state.prescription.milestones.filter((value) => !milestoneIds?.includes(value.id))
                : [];

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    milestones: [...restMilestones, ...(action.milestones ?? [])],
                    allMilestones: action.allMilestones,
                },
            };

        case UPDATE_MILESTONE:
            if (!state.prescription) {
                return state;
            }

            const patientMilestones =
                (state.prescription.allMilestones?.length !== 0
                    ? state.prescription.allMilestones
                    : (state.prescription?.patientHistory as MHCWithMilestone)?.milestones) ?? [];

            const finalAllMilestones = action.milestone
                ? [
                      ...patientMilestones.filter((m) => m.id !== action.milestone?.id),
                      action.milestone,
                  ]
                : patientMilestones;

            const finalCurrentMilestones = action.milestone
                ? [
                      ...(state.prescription.milestones ?? []).filter(
                          (m) => m.id !== action.milestone?.id,
                      ),
                      action.milestone,
                  ]
                : state.prescription.milestones;

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    allMilestones: finalAllMilestones,
                    milestones: finalCurrentMilestones,
                },
            };

        case CLEAR_ADMINISTERED_VACCINES:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    administeredVaccines: undefined,
                    allVaccines: action.allVaccines,
                },
            };

        case SET_MILESTONES:
            if (!state.prescription) {
                return state;
            }

            const { milestones, allMilestones } = action;

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    milestones,
                    allMilestones,
                },
            };

        case UPSERT_MEDICAL_HISTORY_CARD_STATE: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                medicalHistoryCardState: action.cardState,
            };
        }

        case TOGGLE_NO_KNOWN_MEDICAL_HISTORY: {
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                noKnownMedicalHistory: action.toggleState,
            };
        }

        case MAGIC_BOX_ACTIVE_TAB: {
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                magicBoxActiveTab: action.activeTab,
            };
        }

        case DELETE_MEDICAL_HISTORY:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    patientHistory: {
                        ...state.prescription.patientHistory,
                        [action.cat]: cloneAndDelete(
                            action.itemId,
                            (state.prescription.patientHistory || {})[action.cat],
                        ),
                    },
                },
            };

        case CLEAR_MEDICAL_HISTORY:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    patientHistory: {},
                },
            };

        case UPDATE_VITALS_V0:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    vitals: Object.entries(action.vitals).map((v) => v[1]),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };

        case UPDATE_SUGGESTED_DRUGS_LAB_TESTS: {
            return { ...state, suggestedDrugs: action.drugs, suggestedLabTests: action.labTests };
        }
        case UPDATE_RELATED_SYM_DIA: {
            return {
                ...state,
                relatedSymptons: action.symptoms,
                relatedDiagnosis: action.diagnoses,
            };
        }
        case UPSERT_ADVICE: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    advices: cloneAndUpsert(action.advice, state.prescription.advices),
                },
            };
        }
        case EDIT_ADVICE: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    advices: state.prescription.advices?.find(
                        (advice) => advice.id === action.advice.id,
                    )
                        ? cloneAndUpsert(action.advice, state.prescription.advices)
                        : state.prescription.advices,
                },
            };
        }
        case DELETE_ADVICE_PERMANENTLY:
        case DELETE_ADVICE: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    advices: cloneAndDelete(action.id, state.prescription.advices),
                },
            };
        }
        case FINISH_PRESCRIPTION: {
            return { ...state };
        }
        case SANITIZE_FINAL_PRESCRIPTION: {
            return { ...state, prescription: action.px };
        }
        case LOAD_PRESCRIPTION_TEMPLATE: {
            if (!state.prescription) {
                return state;
            }

            const { id, date, doctor, patient, visitId } = state.prescription;
            const {
                diagnosis,
                symptoms,
                medications,
                labTests,
                followup,
                advices,
                labVitals,
                examinations,
                dyform,
                injections,
                procedures,
            } = action.pt;

            const source = `${TRACKING_SOURCE_TEMPLATE}-${action.entity}`;

            const dyForm = cloneAndUpsertArray(state.prescription.dyform || [], dyform);

            dyForm.forEach((i) => {
                i.elements = cloneAndUpsertArray(
                    i.elements || [],
                    dyform?.find((j) => j.id === i.id)?.elements,
                );
            });

            const qtColumnEnabled =
                action.medElements.find((ele) => ele.id === MED_TABLE_COL_QUANTITY_KEY)?.isShown ||
                false;

            const p = {
                ...state.prescription,
                dyform: dyForm,
                diagnosis: cloneAndUpsertArray(
                    state.prescription.diagnosis || [],
                    diagnosis?.map((d) => ({
                        ...d,
                        // TODO
                        // we do not capture properties in the template for sy/dy
                        // this should be removed
                        duration: {
                            custom:
                                d.duration?.custom ||
                                (d.duration?.value
                                    ? d.duration?.value + ' ' + d.duration?.unit
                                    : ''),
                        },

                        track: {
                            source: source,
                        },
                    })),
                ),
                symptoms: cloneAndUpsertArray(
                    state.prescription.symptoms || [],
                    symptoms?.map((s) => ({
                        ...s,
                        // TODO
                        // we do not capture properties in the template for sy/dy
                        // this should be removed
                        duration: {
                            custom:
                                s.duration?.custom ||
                                (s.duration?.value
                                    ? s.duration?.value + ' ' + s.duration?.unit
                                    : ''),
                        },

                        track: {
                            source: source,
                        },
                    })),
                ),
                injections: cloneAndUpsertArray(
                    state.prescription.injections || [],
                    injections?.map((injection) => ({
                        ...injection,
                        track: {
                            source: source,
                        },
                    })),
                ),
                medications: cloneAndUpsertArray(
                    state.prescription.medications || [],
                    medications?.map((m) => ({
                        ...medWithQuantity(sanitizeMed(m, action.medElements, state.medRouteEnabled), qtColumnEnabled),
                        dose: {
                            custom:
                                m.dose?.custom ||
                                (m.dose?.value ? m.dose?.value + ' ' + m.dose?.unit : ''),
                        },
                        duration: {
                            custom:
                                m.duration?.custom ||
                                (m.duration?.value
                                    ? m.duration?.value + ' ' + m.duration?.unit
                                    : ''),
                        },
                        frequency: {
                            type: 'custom',
                            custom: convertToNewField(m.frequency),
                        },

                        track: {
                            source: source,
                        },
                    })),
                ),
                labTests: cloneAndUpsertArray(
                    state.prescription.labTests?.map((l) =>
                        action.pt.isPackage && action.pt.labTests?.find((i) => i.id === l.id)
                            ? {
                                  ...l,
                                  bundle: {
                                      id: action.pt.id,
                                      name: action.pt.name,
                                  },
                              }
                            : l,
                    ) || [],
                    labTests?.map((l) =>
                        action.pt.isPackage
                            ? {
                                  ...l,
                                  track: {
                                      source: source,
                                  },
                                  bundle: {
                                      id: action.pt.id,
                                      name: action.pt.name,
                                  },
                              }
                            : {
                                  ...l,
                                  bundle: undefined,
                                  track: {
                                      source: source,
                                  },
                              },
                    ),
                ),
                labVitals: cloneAndUpsertArray(
                    state.prescription.labVitals || [],
                    labVitals?.map((t) => ({
                        ...toPxLabVitalFromApiObj(t),
                        track: {
                            source: source,
                        },
                    })),
                ),
                examinations: cloneAndUpsertArray(
                    state.prescription.examinations || [],
                    examinations?.map((t) => ({
                        ...t,
                        track: {
                            source: source,
                        },
                    })),
                ),

                followup: { ...createFollowUpFromTemplate(followup), notes: followup?.notes },
                advices: cloneAndUpsertArray(
                    state.prescription.advices || [],
                    advices?.map((l) => ({
                        ...l,
                        track: {
                            source: source,
                        },
                    })),
                ),
                id,
                date,
                doctor,
                patient,
                visitId,
                meta: {
                    templateId: action.pt.id,
                },
                procedures: cloneAndUpsertArray(
                    state.prescription.procedures || [],
                    procedures?.map((t) => ({
                        ...t,
                        track: {
                            source: source,
                        },
                    })),
                ),
            };

            const isUseTemplateDialogVisible = !isPPFilled(p);

            return {
                ...state,
                suggestedDrugs: [],
                prescription: p,
                isUseTemplateDialogVisible,
            };
        }
        case UPDATE_MEDICATIONS: {
            const { medications } = action;
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    medications: (state?.prescription?.medications || []).map((i) =>
                        medications[i.id] ? { ...i, ...medications[i.id] } : i,
                    ),
                },
            };
        }
        case UPDATE_LAB_TESTS: {
            const { labTests } = action;
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    labTests: (state?.prescription?.labTests || []).map((i) =>
                        labTests[i.id] ? { ...i, ...labTests[i.id] } : i,
                    ),
                },
            };
        }
        case SET_DRUGS_SUBSTITUTE: {
            const { substitutes } = action;
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                drugsSubstitute: substitutes,
            };
        }
        case REPLACE_HE_MEDICATION: {
            if (!state.prescription) {
                return state;
            }
            const { substitutes } = action;
            const medications = state.prescription.medications?.map((i) =>
                substitutes[i.id] ? getNewMedicine(i, substitutes[i.id]) : i,
            );
            const idToMedicineObj: { [k: string]: MedicationsEntity } = {};
            medications?.forEach((i) => {
                idToMedicineObj[i.id] = i;
            });
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    medications: Object.values(idToMedicineObj),
                },
            };
        }
        case SET_DOCTOR_PACKAGES: {
            const { doctorPackages } = action;
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                doctorPackages,
            };
        }
        case UPSERT_DENTAL_EXAMINATION: {
            if (!state.prescription) {
                return state;
            }
            if (action.index === undefined) {
                const { id, teeth_id } = action.dentalExamination;
                let currentIndex: number | undefined;
                if (action.dentalExamination.group_id) {
                    currentIndex = state.prescription.dentalExaminations?.findIndex(
                        (i) =>
                            i.id === id &&
                            i.teeth_id === action.dentalExamination.teeth_id &&
                            i.group_id === action.dentalExamination.group_id,
                    );
                    if (Number(currentIndex) > -1) {
                        action.index = currentIndex;
                    } else {
                        action.index = state.prescription.dentalExaminations?.length;
                    }
                } else {
                    currentIndex = state.prescription.dentalExaminations?.findIndex(
                        (i) => i.id === id && i.teeth_id === teeth_id,
                    );
                    if (Number(currentIndex) > -1) {
                        action.index = currentIndex;
                    } else {
                        action.index = state.prescription.dentalExaminations?.length;
                    }
                }
            }
            const { processedDentalExaminations = [] } = state;
            if (!action.bypass) {
                if (action.dentalExamination.group_id) {
                    const currentTeethIndex = processedDentalExaminations.findIndex(
                        (i) => i.group?.id === action.dentalExamination.group_id,
                    );
                    let teeth = action.dentalExamination.teeth_id || '';
                    const surfaces = action.dentalExamination.surfaces || [];
                    teeth += surfaces.length > 0 ? `@${surfaces.map((i) => i.id).join(',')}` : '';
                    const surfaceSet = new Set(surfaces?.map((i) => i.id));
                    if (currentTeethIndex === -1) {
                        processedDentalExaminations.push({
                            teeth: teeth,
                            surfaces: surfaceSet,
                            group: {
                                id: action.dentalExamination.group_id,
                                data: {
                                    [action.dentalExamination.teeth_id || '']: surfaceSet,
                                },
                            },
                        });
                    } else {
                        const currentTeeth = processedDentalExaminations.find(
                            (i) => i.group?.id === action.dentalExamination.group_id,
                        );
                        if (currentTeeth) {
                            currentTeeth.teeth += teeth;
                            surfaces.forEach((i) => currentTeeth.surfaces.add(i.id));
                            currentTeeth.group = {
                                id: action.dentalExamination.group_id,
                                data: {
                                    ...currentTeeth.group?.data,
                                    [action.dentalExamination.teeth_id || '']: surfaceSet,
                                },
                            };
                        }
                    }
                } else {
                    const currentTeethIndex = processedDentalExaminations.findIndex(
                        (i) => i.teeth === action.dentalExamination.teeth_id,
                    );
                    if (currentTeethIndex === -1) {
                        processedDentalExaminations.push({
                            teeth: action.dentalExamination.teeth_id || '',
                            surfaces: new Set(action.dentalExamination.surfaces?.map((i) => i.id)),
                        });
                    }
                }
            }
            return {
                ...state,
                processedDentalExaminations,
                prescription: {
                    ...state.prescription,
                    dentalExaminations:
                        action.index !== undefined && state.prescription.dentalExaminations
                            ? ((state.prescription.dentalExaminations[action.index] =
                                  action.dentalExamination),
                              [...state.prescription.dentalExaminations])
                            : cloneAndUpsert(
                                  action.dentalExamination,
                                  state.prescription.dentalExaminations,
                              ),
                },
            };
        }
        case DELETE_DENTAL_EXAMINATION: {
            if (!state.prescription) {
                return state;
            }
            let dentalExaminations: DentalExamination[] | undefined = [];
            if (action.group) {
                const dentalExamination = state.prescription.dentalExaminations?.find(
                    (i) => i.entity_id === action.id,
                );
                const indexSet = new Set();
                state.prescription.dentalExaminations?.forEach((i, index) => {
                    if (i.group_id === action.group?.id && dentalExamination?.id === i.id) {
                        indexSet.add(index);
                    }
                });
                dentalExaminations = state.prescription.dentalExaminations?.filter(
                    (i, index) => !indexSet.has(index),
                );
            } else {
                dentalExaminations =
                    action.index !== undefined && state.prescription.dentalExaminations
                        ? (state.prescription.dentalExaminations.splice(action.index, 1),
                          [...state.prescription.dentalExaminations].filter(Boolean))
                        : cloneAndDelete(action.id, state.prescription.dentalExaminations);
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dentalExaminations,
                },
            };
        }
        case EDIT_DENTAL_EXAMINATIONS_GROUP: {
            if (!state.prescription) {
                return state;
            }
            const { old_group_id, new_group_id } = action;
            const { dentalExaminations } = state.prescription;

            const dentalExaminationsObject = getAllOldAndNewDentalExaminations(
                old_group_id,
                dentalExaminations,
            );
            const { oldDentalExaminations } = dentalExaminationsObject;
            let { newDentalExaminations } = dentalExaminationsObject;

            const newTeethAndSurfacesObject = getNewTeethAndSurfacesObject(new_group_id);

            newDentalExaminations = getNewDentalExaminations(
                new_group_id,
                oldDentalExaminations,
                newTeethAndSurfacesObject,
                newDentalExaminations,
            );

            const processedDentalExaminations = getNewProcessedDentalExaminations(
                new_group_id,
                old_group_id,
                state.processedDentalExaminations,
            );

            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dentalExaminations: newDentalExaminations,
                },
                processedDentalExaminations,
            };
        }
        case DELETE_DENTAL_EXAMINATIONS: {
            if (!state.prescription) {
                return state;
            }
            let dentalExaminations: DentalExamination[] | undefined = [];
            if (action.group) {
                dentalExaminations = state.prescription.dentalExaminations?.filter(
                    (i) => i.group_id !== action.group?.id,
                );
            } else {
                dentalExaminations = state.prescription.dentalExaminations?.filter(
                    (i) => i.teeth_id !== action.teeth_id || i.group_id,
                );
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dentalExaminations,
                },
            };
        }
        case COPY_DENTAL_EXAMINATION_DATE:
            if (!state.prescription) {
                return state;
            }
            let dentalExaminations: DentalExamination[] | undefined = [];

            if (action.group) {
                dentalExaminations = state.prescription.dentalExaminations?.map((i) =>
                    i.group_id === action.group?.id
                        ? {
                              ...i,
                              since: action.since,
                          }
                        : i,
                );
            } else {
                dentalExaminations = state.prescription.dentalExaminations?.map((i) =>
                    i.teeth_id === action.teeth_id
                        ? {
                              ...i,
                              since: action.since,
                          }
                        : i,
                );
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dentalExaminations,
                },
            };

        case UPSERT_DENTAL_PROCEDURE: {
            if (!state.prescription) {
                return state;
            }
            if (action.index === undefined) {
                action.index = state.prescription.dentalProcedures?.length;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dentalProcedures:
                        action.index !== undefined && state.prescription.dentalProcedures
                            ? ((state.prescription.dentalProcedures[action.index] =
                                  action.dentalProcedure),
                              [...state.prescription.dentalProcedures])
                            : cloneAndUpsert(
                                  action.dentalProcedure,
                                  state.prescription.dentalProcedures,
                              ),
                },
            };
        }
        case UPSERT_DENTAL_PROCEDURES: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dentalProcedures: action.dentalProcedures,
                },
            };
        }
        case DELETE_DENTAL_PROCEDURE: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dentalProcedures:
                        action.index !== undefined && state.prescription.dentalProcedures
                            ? (state.prescription.dentalProcedures.splice(action.index, 1),
                              [...state.prescription.dentalProcedures].filter(Boolean))
                            : cloneAndDelete(action.id, state.prescription.dentalProcedures),
                },
            };
        }
        case COPY_DENTAL_PROCEDURE_DATE:
            if (!state.prescription) {
                return state;
            }
            let dentalProcedures: DentalProcedure[] | undefined = [];
            const { service_id, start_on } = action;
            if (service_id) {
                dentalProcedures = state.prescription.dentalProcedures?.map((i) =>
                    i.service_id === service_id
                        ? {
                              ...i,
                              start_on: start_on,
                          }
                        : i,
                );
            } else {
                dentalProcedures = state.prescription.dentalProcedures?.map((i) => ({
                    ...i,
                    start_on: start_on,
                }));
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    dentalProcedures: dentalProcedures,
                },
            };
        case SET_CHART_TYPE:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                chartType: action.chartType,
            };
        case SET_PROCESSED_DENTAL_EXAMINATIONS:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                processedDentalExaminations: action.processedDentalExaminations,
            };
        case SET_PATIENT_HEALTH_SUMMARY:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                patientHealthSummary: action.patientHealthSummary,
                isFetchingPatientHealthSummary: false,
            };
        case SET_ONGOING_TREATMENT_PLANS:
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                ongoingTreatmentPlans: action.ongoingTreatmentPlans,
            };
        case SET_INJECTION_ELEMENTS: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    injections: action.injectionElements,
                },
            };
        }
        case DELETE_INJECTION: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    injections: cloneAndDelete(action.injection.id, state.prescription.injections),
                },
                isUseTemplateDialogVisible: false,
                toastVisible: undefined,
            };
        }
        case UPSERT_IPD_ADMISSION: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    ipdAdmission: action.ipdAdmission,
                },
            };
        }
        case UPSERT_CARE_CANVAS: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    careCanvas: action.careCanvas,
                },
            };
        }
        case UPSERT_CURRENT_MEDICATIONS: {
            if (!state.prescription) {
                return state;
            }
            return {
                ...state,
                prescription: {
                    ...state.prescription,
                    patientHistory: {
                        ...state.prescription.patientHistory,
                        currentMedications: cloneAndUpsertArray(
                            state.prescription.patientHistory?.currentMedications || [],
                            action.currentMedications,
                        ),
                    },
                },
            };
        }
        case UPSERT_RELATED_PROFILE_DATA:
            if (!state.prescription) {
                return state;
            }

            return {
                ...state,
                relatedProfilesData: {
                    ...state.relatedProfilesData,
                    [action.pid]: {
                        ...state.relatedProfilesData?.[action.pid],
                        ...action.details,
                    },
                },
            };
        case SET_MED_ROUTE_ENABLED:
            return {
                ...state,
                medRouteEnabled: action.medRouteEnabled,
            };
        default:
            return state;
    }
};

export const upsertedRx = (
    oldPrescription: Prescription,
    tool: PushVisitTool,
    dyform?: PadDForm[],
    newDentalExaminations?: DentalExamination[] | undefined,
    careCanvas?: CareCanvas[] | undefined,
    draftId?: string,
    allVaccs? :IVaccineStatus[],
    newVaccs?: IVaccineStatus[],
    follow_up?: Followup,
): Prescription => {
    return {
        ...oldPrescription,
        dyform: dyform || oldPrescription.dyform,
        diagnosis: cloneAndUpsertArray(oldPrescription.diagnosis || [], tool.diagnosis),
        symptoms: cloneAndUpsertArray(oldPrescription.symptoms || [], tool.symptoms),
        medications: cloneAndUpsertArray(oldPrescription.medications || [], tool.medications),
        injections: cloneAndUpsertArray(oldPrescription.injections || [], tool.injections),
        labTests: cloneAndUpsertArray(oldPrescription.labTests || [], tool.labTests),
        labVitals: cloneAndUpsertArray(oldPrescription.labVitals || [], tool.labVitals),
        followup: follow_up || oldPrescription.followup || tool.followup,
        advices: cloneAndUpsertArray(oldPrescription.advices || [], tool.advices),
        prescriptionNotes: oldPrescription.prescriptionNotes || tool.prescriptionNotes,
        privateNotes: oldPrescription.privateNotes || tool.privateNotes,
        examinations: cloneAndUpsertArray(
            oldPrescription.examinations || [],
            tool.medicalHistory?.examinations,
        ),
        vitals: cloneAndUpsertArray(oldPrescription.vitals || [], tool.medicalHistory?.vitals),
        patientHistory: {
            patientMedicalConditions: cloneAndUpsertArray(
                oldPrescription.patientHistory?.patientMedicalConditions || [],
                tool.medicalHistory?.patientHistory?.patientMedicalConditions,
            ),
            familyHistory: cloneAndUpsertArray(
                oldPrescription.patientHistory?.familyHistory || [],
                tool.medicalHistory?.patientHistory?.familyHistory,
            ),
            lifestyleHabits: cloneAndUpsertArray(
                oldPrescription.patientHistory?.lifestyleHabits || [],
                tool.medicalHistory?.patientHistory?.lifestyleHabits,
            ),
            recentTravelHistory: cloneAndUpsertArray(
                oldPrescription.patientHistory?.recentTravelHistory || [],
                tool.medicalHistory?.patientHistory?.recentTravelHistory,
            ),
            currentMedications: cloneAndUpsertArray(
                oldPrescription.patientHistory?.currentMedications || [],
                tool.medicalHistory?.patientHistory?.currentMedications,
            ),
            drugAllergy: cloneAndUpsertArray(
                oldPrescription.patientHistory?.drugAllergy || [],
                tool.medicalHistory?.patientHistory?.drugAllergy,
            ),
            foodOtherAllergy: cloneAndUpsertArray(
                oldPrescription.patientHistory?.foodOtherAllergy || [],
                tool.medicalHistory?.patientHistory?.foodOtherAllergy,
            ),
            pastProcedures: cloneAndUpsertArray(
                oldPrescription.patientHistory?.pastProcedures || [],
                tool.medicalHistory?.patientHistory?.pastProcedures,
            ),
            otherMedicalHistory: cloneAndUpsertArray(
                oldPrescription.patientHistory?.otherMedicalHistory || [],
                tool.medicalHistory?.patientHistory?.otherMedicalHistory,
            ),
        },
        milestones: cloneAndUpsertArray(oldPrescription.milestones || [], tool.milestones),
        administeredVaccines: newVaccs ?? cloneAndUpsertArray(
            oldPrescription.administeredVaccines || [],
            tool.administeredVaccines,
        ),
        allMilestones: cloneAndUpsertArray(
            oldPrescription.allMilestones?.length
                ? oldPrescription.allMilestones
                : (oldPrescription.patientHistory as MHCWithMilestone)?.milestones || [],
            tool.allMilestones,
        ),
        allVaccines:
            allVaccs ??
            cloneAndUpsertArray(
                oldPrescription.allVaccines?.length
                    ? oldPrescription.allVaccines
                    : (oldPrescription.patientHistory as MHCWithVaccination)?.vaccinationHistory ||
                          [],
                tool.vaccinationHistory,
            ),
        meta: {
            ...oldPrescription.meta,
            draftId: draftId || oldPrescription.meta?.draftId,
        },
        refer: oldPrescription.refer || tool.refer,
        dentalExaminations: newDentalExaminations || oldPrescription.dentalExaminations,
        dentalProcedures: cloneAndUpsertArray(
            oldPrescription.dentalProcedures || [],
            tool.dentalProcedures,
        ),
        opVision: cloneAndUpsertArray(oldPrescription.opVision || [], tool.opVision),
        opIop: cloneAndUpsertArray(oldPrescription.opIop || [], tool.opIop),
        opCurrentSpec: cloneAndUpsertArray(oldPrescription.opCurrentSpec || [], tool.opCurrentSpec),
        opSubjectiveRefraction: cloneAndUpsertArray(
            oldPrescription.opSubjectiveRefraction || [],
            tool.opSubjectiveRefraction,
        ),
        opAutoRefraction: cloneAndUpsertArray(
            oldPrescription.opAutoRefraction || [],
            tool.opAutoRefraction,
        ),
        opFinalPrescription: cloneAndUpsertArray(
            oldPrescription.opFinalPrescription || [],
            tool.opFinalPrescription,
        ),
        procedures: cloneAndUpsertArray(oldPrescription.procedures || [], tool.procedures),
        careCanvas: careCanvas || oldPrescription.careCanvas,
    };
};

function isPPFilled(p: Prescription) {
    const {
        symptoms,
        diagnosis,
        medications,
        injections,
        labTests,
        labVitals,
        opVision,
        opIop,
        opCurrentSpec,
        opSubjectiveRefraction,
        opAutoRefraction,
        opFinalPrescription,
        procedures,
    } = p;
    return (
        (symptoms && symptoms.length > 0) ||
        (diagnosis && diagnosis.length > 0) ||
        (medications && medications.length > 0) ||
        (labTests && labTests.length > 0) ||
        (labVitals && labVitals.length > 0) ||
        (opVision && opVision.length > 0) ||
        (opIop && opIop.length > 0) ||
        (opCurrentSpec && opCurrentSpec.length > 0) ||
        (opSubjectiveRefraction && opSubjectiveRefraction.length > 0) ||
        (opAutoRefraction && opAutoRefraction.length > 0) ||
        (opFinalPrescription && opFinalPrescription.length > 0) ||
        (injections && injections.length > 0) ||
        (procedures && procedures.length > 0)
    );
}

export function createFollowUpFromTemplate(
    followup: { msFromNow?: number | undefined; optionId?: string | undefined } | undefined,
): Followup | undefined {
    return followup?.msFromNow
        ? {
              date: new Date(
                  new Date(new Date().toDateString()).getTime() + followup.msFromNow,
              ).toISOString(),
              optionId: followup.optionId,
          }
        : undefined;
}

const migrateVitalsToBe = (px: Prescription): Prescription => {
    const { vitals } = px;
    if (!vitals || vitals.length === 0) {
        return px;
    }

    const migratedVitals: (VitalsEntity | undefined)[] = vitals.map((pxVital) => {
        const beVitalMappingItem = OLD_HARDCODED_VITALS_TO_NEW_MAPPING[pxVital.id];

        if (beVitalMappingItem) {
            const beVital = VITALS.find((v) => v.id === beVitalMappingItem.vId);

            if (!beVital || !beVital.units_with_ranges) {
                console.log('What is wrong, incomplete vitals mapping');
                return;
            }

            const usedUnit = beVital.units_with_ranges.find(
                (u) => u.code_id === beVitalMappingItem.unitCodeId,
            );

            if (!usedUnit) {
                console.log('What is wrong, incomplete vitals unit mapping');
                return;
            }

            return {
                id: beVital.id,
                name: beVital.name,
                dis_name: beVital.dis_name,
                value: {
                    qt: pxVital.value.qt,
                    unit: usedUnit.dis_unit,
                    code: usedUnit.code,
                    code_id: usedUnit.code_id,
                    safe: pxVital.value.safe,
                },
                interpretation: pxVital.interpretation,
                // we made arelease for .date as key for a day so make it backward compatiblie
                isoDate: pxVital.isoDate || pxVital.date,
                remark: pxVital.remark,
                locked: pxVital.locked,
            };
        }

        // local vital created
        return {
            id: pxVital.id,
            name: pxVital.name,
            dis_name: pxVital.dis_name,
            value: {
                qt: pxVital.value.qt,
                unit: pxVital.value.unit,
                code: pxVital.value.code,
                code_id: pxVital.value.code_id,
                safe: pxVital.value.safe,
            },
            interpretation: pxVital.interpretation,
            // we made arelease for .date as key for a day so make it backward compatiblie
            isoDate: pxVital.isoDate || pxVital.date,
            remark: pxVital.remark,
            locked: pxVital.locked,
        };
    });
    const filteredVitals = migratedVitals.filter(Boolean) as VitalsEntity[];
    const updatedVitals = changeVitalIdFromVToLB(filteredVitals);
    return {
        ...px,
        // Not sure, why ts is not decoding .filter
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        vitals: updatedVitals,
    };
};

const getSyDyEntity = (i: DiagnosisEntityOrSymptomsEntity, since: SelectionEntity | Duration) => {
    const { value, unit } = since;
    if (i.id.startsWith(LOCAL_ID_PREFIX))
        return {
            ...i,
            duration: {
                value,
                unit,
                custom: value + ' ' + unit,
            },
        };
    return {
        ...i,
        properties: {
            ...i.properties,
            [SINCE_ID]: {
                name: 'Since',
                selection: [
                    {
                        value: value as string,
                        unit: unit as string,
                        id: options[since.unit?.toLowerCase() as string],
                    },
                ],
            },
        },
    };
};

function getNewMedicine(
    currentMedicine: MedicationsEntity,
    newMedicine: CodedMedicine,
): MedicationsEntity {
    const {
        name,
        eka_id,
        drug_form,
        generic_name,
        generic_id,
        generic_composition_id,
        type,
        manufacturer_name,
        dosage,
        soa,
        product_type,
        index,
    } = newMedicine;
    const {
        form: current_drug_form,
        generic_name: current_drug_generic_name,
        generic_id: current_drug_generic_id,
        dosage_form,
        dose,
        df_id,
        frequency,
        timing,
        duration,
        product_type: current_product_type,
        soa: current_soa,
    } = currentMedicine;

    const newMedicineObj = {
        ...currentMedicine,
        id: eka_id,
        name: name,
        form: current_drug_form || drug_form,
        generic_name: generic_name || current_drug_generic_name,
        generic_id: generic_id || current_drug_generic_id || generic_composition_id,
        type: type,
        manufacturer_name: manufacturer_name,
        dosage_form: dosage_form || dosage?.dosage_form || '',
        dose: {
            value: dose?.value || dosage?.unit_val,
            unit: dose?.unit || dosage?.unit_name,
            id: dose?.id || dosage?.unit_id,
        },
        df_id: df_id || dosage?.df_id,
        frequency: {
            type: 'custom',
            custom: frequency?.custom || dosage?.dosage || '',
        },
        timing:
            timing || (dosage?.food ? DRUG_DOSAGE_DURATION_TIMING_ENUM[dosage?.food] || '' : ''),
        duration: {
            value: duration?.value || dosage?.days || undefined,
            unit: duration?.unit || (dosage?.days ? 'Days' : undefined),
            custom:
                duration?.custom ||
                (dosage?.days
                    ? `${dosage?.days || ''} ${getUnit('Days', dosage?.days)}`
                    : undefined),
        },
        product_type: current_product_type || product_type,
        soa: current_soa || soa || false,
        hxng_mapped: HXNG_MAPPED.YES,
        hxng_status: HXNG_STATUS.AVAILABLE,
        track: {
            label: POPUP,
            index: index,
            source: SUBSTITUTE,
        },
    };
    return newMedicineObj;
}

function getChartType(prescription: Prescription): ChartType {
    const ageObj = prescription.patient.profile.personal.age || {};
    const age = getAgeInYears(ageObj) || 18;
    const chartType =
        age <= 6 ? ChartTypeEnum.PEDIA : age <= 13 ? ChartTypeEnum.MIXED : ChartTypeEnum.ADULT;
    return chartType;
}

function getProcessedDentalExaminations(prescription: Prescription): ProcessedDentalExaminations[] {
    const tToDObj: {
        [k: string]: {
            teeth: string;
            surfaces: Set<string>;
            group?: {
                id: string;
                data: KeyToSetObject;
            };
        };
    } = {};
    const { dentalExaminations = [] } = prescription;
    dentalExaminations.forEach((i) => {
        const groupId = i.group_id;
        if (groupId) {
            const groupedDentalExaminations = dentalExaminations.filter(
                (i) => String(i.group_id) === groupId,
            );
            const teethArray = Array.from(
                new Set(groupedDentalExaminations.map((i) => i.teeth_id || '').sort()),
            );
            const tToSObj: KeyToSetObject = {};
            groupedDentalExaminations.forEach((i) => {
                const teethId = i.teeth_id || '';
                if (tToSObj[teethId]) i.surfaces?.forEach((i) => tToSObj[teethId].add(i.id));
                else tToSObj[teethId] = new Set(i.surfaces?.map((i) => i.id) || []);
            });
            let teeth = '';
            const surfaceSet: Set<string> = new Set();
            teethArray.forEach((i, index) => {
                teeth += i;
                const surfaces = Array.from(tToSObj[i]).filter(Boolean).sort();
                teeth += surfaces.length > 0 ? `@${surfaces.join(',')}` : '';
                if (teethArray.length - 1 > index) teeth += '|';
                surfaces.forEach((surface) => surfaceSet.add(surface));
            });
            tToDObj[teeth] = {
                teeth,
                surfaces: surfaceSet,
                group: {
                    id: groupId,
                    data: tToSObj,
                },
            };
        } else {
            const teethId = i.teeth_id || '';
            if (tToDObj[teethId]?.surfaces) {
                i.surfaces?.forEach((i) => tToDObj[teethId].surfaces.add(i.id));
            } else {
                tToDObj[teethId] = {
                    teeth: teethId,
                    surfaces: new Set(i?.surfaces?.map((i) => i.id) || []),
                };
            }
        }
    });
    const processedDentalExaminations: ProcessedDentalExaminations[] = [];
    for (const key in tToDObj) {
        const { teeth, surfaces, group } = tToDObj[key];
        const obj = {
            teeth,
            surfaces,
            group,
        };
        processedDentalExaminations.push(obj);
    }
    return processedDentalExaminations;
}

function getAllOldAndNewDentalExaminations(
    old_group_id: string,
    dentalExaminations?: DentalExamination[],
) {
    const allOldDentalExaminations = dentalExaminations?.filter((i) => i.group_id === old_group_id);
    const oldDentalExaminations = uniqBy(allOldDentalExaminations, 'id');
    const newDentalExaminations = dentalExaminations?.filter((i) => i.group_id !== old_group_id);
    return { oldDentalExaminations, newDentalExaminations };
}

function getNewTeethAndSurfacesObject(new_group_id: string) {
    const newTeethAndSurfacesObject: TeethToSurfacesObject = {};
    const newGroupIdArray = new_group_id.split('|');
    newGroupIdArray.forEach((i) => {
        const [teeth, surfaces] = i.split('@');
        const surfacesArray = surfaces
            ?.split(',')
            ?.map((i) => ({ id: i, name: allSurfacesIdToName[i] }));
        newTeethAndSurfacesObject[teeth] = surfacesArray;
    });
    return newTeethAndSurfacesObject;
}

function getNewDentalExaminations(
    new_group_id: string,
    oldDentalExaminations: DentalExamination[],
    newTeethAndSurfacesObject: TeethToSurfacesObject,
    newDentalExaminations?: DentalExamination[],
) {
    if (!Array.isArray(newDentalExaminations)) {
        return [];
    }
    const group_id = new_group_id.split('|').length > 1 ? new_group_id : undefined;
    oldDentalExaminations.forEach((i) => {
        for (const key in newTeethAndSurfacesObject) {
            const currentIndex = newDentalExaminations?.findIndex(
                (e) =>
                    e.id === i.id &&
                    e.teeth_id === key &&
                    (e.group_id ? e.group_id === new_group_id : true),
            );
            if (Number(currentIndex) > -1) {
                newDentalExaminations[currentIndex] = {
                    ...newDentalExaminations[currentIndex],
                    surfaces: newTeethAndSurfacesObject[key],
                    group_id: group_id,
                    since: i.since || newDentalExaminations[currentIndex].since,
                    remark: i.remark || newDentalExaminations[currentIndex].remark,
                };
            } else {
                newDentalExaminations.push({
                    ...i,
                    teeth_id: key,
                    surfaces: newTeethAndSurfacesObject[key],
                    group_id: group_id,
                });
            }
        }
    });
    return newDentalExaminations;
}

function getNewProcessedDentalExaminations(
    new_group_id: string,
    old_group_id: string,
    processedDentalExaminations?: ProcessedDentalExaminations[],
) {
    if (!Array.isArray(processedDentalExaminations)) {
        return [];
    }
    const currentTeethIndex = processedDentalExaminations.findIndex(
        (i) => i.group?.id === old_group_id,
    );
    const newGroupIdArray = new_group_id.split('|');
    const addGroup = newGroupIdArray.length > 1;
    const surfacesSet: Set<string> = new Set();
    const tToSObj: KeyToSetObject = {};
    newGroupIdArray.forEach((i) => {
        const [teeth, surfaces] = i.split('@');
        const surfacesArray = surfaces?.split(',');
        tToSObj[teeth] = new Set(surfacesArray);
        surfacesArray?.forEach((i) => surfacesSet.add(i));
    });
    processedDentalExaminations[currentTeethIndex] = {
        teeth: new_group_id,
        surfaces: surfacesSet,
        group: addGroup
            ? {
                  id: new_group_id,
                  data: tToSObj,
              }
            : undefined,
    };
    return uniqBy(processedDentalExaminations, 'teeth');
}
